import React, { useEffect, useState } from "react";
import {
    Badge,
    Button,
    HStack,
    Input,
    Select,
    SimpleGrid,
    Spinner,
    Text,
    VStack,
    useToast,
} from "@chakra-ui/react";
import FinancePageShell from "./common/FinancePageShell";
import FinanceSectionCard from "./common/FinanceSectionCard";
import FinanceStatCard from "./common/FinanceStatCard";
import FinanceDataTable, {
    FinanceDataTableColumn,
} from "./common/FinanceDataTable";
import { financeApi } from "./financeApi";
import { formatDateTime, formatMoney } from "./common/formatters";
import { useCustomTheme } from "../../contexts/CustomThemeContext";

const AdminLedgerAuditPage: React.FC = () => {
    const toast = useToast();
    const { theme } = useCustomTheme();

    const [loading, setLoading] = useState(true);
    const [rows, setRows] = useState<any[]>([]);
    const [summary, setSummary] = useState<any>({});
    const [query, setQuery] = useState("");
    const [entryType, setEntryType] = useState("");
    const [entityType, setEntityType] = useState("");

    const load = async () => {
        try {
            setLoading(true);

            const res = await financeApi.getLedgerEntries({
                q: query,
                entryType,
                entityType,
            });

            setRows(res?.data || []);
            setSummary(res?.summary || {});
        } catch (error: any) {
            toast({
                status: "error",
                title: "Failed to load ledger",
                description:
                    error?.response?.data?.message ||
                    error?.message ||
                    "Please try again.",
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const columns: Array<FinanceDataTableColumn<any>> = [
        {
            key: "entry",
            header: "Entry",
            render: (row) => <Badge>{row.entryType}</Badge>,
        },
        {
            key: "entity",
            header: "Entity",
            render: (row) => (
                <>
                    <Text fontWeight="700">{row.entityType}</Text>
                    <Text fontSize="xs" color={theme.textMuted}>
                        {row.entityId}
                    </Text>
                </>
            ),
        },
        {
            key: "beneficiary",
            header: "Beneficiary",
            render: (row) => row.sellerUserId || row.driverId || "—",
        },
        {
            key: "direction",
            header: "Direction",
            render: (row) => (
                <Badge colorScheme={row.direction === "credit" ? "green" : "orange"}>
                    {row.direction}
                </Badge>
            ),
        },
        {
            key: "amount",
            header: "Amount",
            isNumeric: true,
            render: (row) => formatMoney(row.amount, row.currency),
        },
        {
            key: "note",
            header: "Note",
            render: (row) => row.note || "—",
        },
        {
            key: "created",
            header: "Created",
            render: (row) => formatDateTime(row.createdAt),
        },
    ];

    return (
        <FinancePageShell
            title="Ledger Audit"
            subtitle="Every payment, reserve, payout, retry, cancellation, refund, and reversal recorded for audit."
        >
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                <FinanceStatCard label="Entries" value={String(summary?.count || 0)} />
                <FinanceStatCard label="Credits" value={formatMoney(summary?.credits)} />
                <FinanceStatCard label="Debits" value={formatMoney(summary?.debits)} />
            </SimpleGrid>

            <FinanceSectionCard title="Filters">
                <HStack spacing={3} flexWrap="wrap">
                    <Input
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Search entity, seller, driver, note..."
                        bg={theme.inputBg}
                        borderColor={theme.inputBorder}
                        maxW={{ base: "100%", md: "320px" }}
                    />

                    <Select
                        value={entryType}
                        onChange={(e) => setEntryType(e.target.value)}
                        placeholder="All entry types"
                        bg={theme.inputBg}
                        borderColor={theme.inputBorder}
                        maxW={{ base: "100%", md: "260px" }}
                    >
                        <option value="payment_received">payment_received</option>
                        <option value="seller_payable_reserved">
                            seller_payable_reserved
                        </option>
                        <option value="platform_fee_reserved">platform_fee_reserved</option>
                        <option value="gateway_fee_reserved">gateway_fee_reserved</option>
                        <option value="seller_payable_reversed">
                            seller_payable_reversed
                        </option>
                        <option value="refund_recorded">refund_recorded</option>
                        <option value="seller_payout_sent">seller_payout_sent</option>
                        <option value="seller_payout_failed">seller_payout_failed</option>
                        <option value="seller_payout_cancelled">
                            seller_payout_cancelled
                        </option>
                        <option value="seller_payout_retry">seller_payout_retry</option>
                        <option value="driver_earning_reserved">
                            driver_earning_reserved
                        </option>
                        <option value="driver_payout_sent">driver_payout_sent</option>
                        <option value="driver_payout_failed">driver_payout_failed</option>
                        <option value="driver_payout_cancelled">
                            driver_payout_cancelled
                        </option>
                        <option value="driver_payout_retry">driver_payout_retry</option>
                        <option value="payout_batch_created">payout_batch_created</option>
                        <option value="payout_batch_cancelled">
                            payout_batch_cancelled
                        </option>
                    </Select>

                    <Select
                        value={entityType}
                        onChange={(e) => setEntityType(e.target.value)}
                        placeholder="All entity types"
                        bg={theme.inputBg}
                        borderColor={theme.inputBorder}
                        maxW={{ base: "100%", md: "220px" }}
                    >
                        <option value="order">order</option>
                        <option value="delivery">delivery</option>
                        <option value="hire_booking">hire_booking</option>
                        <option value="accommodation_booking">
                            accommodation_booking
                        </option>
                        <option value="payout_batch">payout_batch</option>
                        <option value="payout_item">payout_item</option>
                    </Select>

                    <Button onClick={load}>Apply</Button>
                </HStack>
            </FinanceSectionCard>

            <FinanceSectionCard title="Ledger entries">
                {loading ? (
                    <VStack py={10}>
                        <Spinner />
                        <Text color={theme.textMuted}>Loading ledger entries...</Text>
                    </VStack>
                ) : (
                    <FinanceDataTable
                        rows={rows}
                        columns={columns}
                        getRowKey={(row, index) => String(row._id || index)}
                        emptyText="No ledger entries found."
                        initialPageSize={10}
                    />
                )}
            </FinanceSectionCard>
        </FinancePageShell>
    );
};

export default AdminLedgerAuditPage;